import { ITEM_CARD_SEQUENCE_LIST_VIEW, SPREADSHEET_VIEW, TIMELINE_VIEW } from "../../constants/identifiers";
import { definitionSelectors } from "../../modules/definition";
import { itemSelectors } from "../../modules/items";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { getSwitchViewInstruction, getTipIdGenerator, Placeholders } from "../utils";
import getSharedTip, { SharedTipIdentifiers } from "./sharedTips";

const isDesktop = preprocessorQuery.isDesktop();
const isTablet = preprocessorQuery.isTablet();
const isMobile = preprocessorQuery.isMobile();

// -----------------------------------------------------
// IMPORTANT NOTES:
//
// GROUP_GATE / accessGate:
//    * expects a function with signature:
//        state => { return true; }
//    * return true to show tutorial
//    * return false to block tutorial and show message
//    * if function is null, will show tutorial
//
// resolve:
//    * expects a function with signature:
//       (action, newState, oldState) => { return true; }
//    * return true to mark the tip as resolved
//    * batch actions will be broken down by the time
//      this function is called, so you will receive the
//      individual actions
//
// -----------------------------------------------------

// Group ID
const GROUP_ID = "BUILD_A_TIMELINE";
const GROUP_LABEL = "Build a Timeline";
const GROUP_DESCRIPTION = `Build a visual timeline representing key ${Placeholders.EVENT_LABEL} in your project.`;
const GROUP_GATE = null;
const GROUP_GATE_MESSAGE = null;
const GROUP_APPLICABLE_GATE = null;
const GROUP_APPLICABLE_GATE_MESSAGE = null;
const GROUP_COMPLETED_MESSAGE =
  "Great work! Keep expanding your project ideas, and pick up from the next tutorial when you are ready.";
const GROUP_LINKS = [
  {
    label: "Learn other ways to create events",
    path: "https://help.timeline.app/article/222-how-to-add-items-to-your-timeline"
  },
  { label: "User Interface Overview", path: "https://help.timeline.app/article/141-user-interface-overview" },
  {
    label: "Chronological vs Narrative Order",
    path: "https://help.timeline.app/article/149-chronological-vs-narrative-order"
  }
];

// Create an ID generator
// this will make IDs of the form: "GROUP_ID / TIP_ID"
const idGenerator = getTipIdGenerator(GROUP_ID);

let tips = [];
if (isDesktop) {
  tips = [
    {
      id: idGenerator("ADD_INITIAL_EVENTS"),
      label: `Add ${Placeholders.EVENT_PLURAL_LABEL} that are important in your project`,
      steps: [
        getSwitchViewInstruction(SPREADSHEET_VIEW),
        { label: 'Click in the "Type label to create new item" cell.', isNumbered: true },
        { label: `Type a label for your first ${Placeholders.EVENT_LABEL} and press enter.`, isNumbered: true },
        { label: `Repeat to add more ${Placeholders.EVENT_PLURAL_LABEL} to your project.`, isNumbered: true },
        { label: "Don't worry about order yet... just get your ideas down!", isNumbered: false }
      ],
      accessGate: null,
      gateMessage: null,
      canSkip: false,
      requiresDismissal: true,
      actionInstruction: `Add at least 5 ${Placeholders.EVENT_PLURAL_LABEL} to continue`,
      dismissalGate: state => {
        // At least 5 events
        const eventTypeId = definitionSelectors.getDefaultSequenceType(state);
        const itemsOfType = itemSelectors.getItemIdsOfType(state, eventTypeId);
        const itemCount = itemsOfType.size;
        return itemCount >= 5;
      }
    },
    getSharedTip(SharedTipIdentifiers.ORGANIZE_EVENTS, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.DRAG_TO_TIMELINE, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.DRAG_TO_RESIZE, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.SET_EVENT_DATE_TIMELINE, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.SET_EVENT_DATE_INSPECTOR, GROUP_ID)
  ];
} else if (isTablet) {
  tips = [
    {
      id: idGenerator("ADD_INITIAL_EVENTS"),
      label: `Add ${Placeholders.EVENT_PLURAL_LABEL} that are important in your project`,
      steps: [
        getSwitchViewInstruction(SPREADSHEET_VIEW),
        { label: 'Double tap in the "Type label to create new item" cell.', isNumbered: true },
        { label: `Type a label for your first ${Placeholders.EVENT_LABEL} and press enter.`, isNumbered: true },
        { label: `Repeat to add more ${Placeholders.EVENT_PLURAL_LABEL} to your project.`, isNumbered: true },
        { label: "Don't worry about order yet... just get your ideas down!", isNumbered: false }
      ],
      accessGate: null,
      gateMessage: null,
      canSkip: false,
      requiresDismissal: true,
      actionInstruction: `Add at least 5 ${Placeholders.EVENT_PLURAL_LABEL} to continue`,
      dismissalGate: state => {
        // At least 5 events
        const eventTypeId = definitionSelectors.getDefaultSequenceType(state);
        const itemsOfType = itemSelectors.getItemIdsOfType(state, eventTypeId);
        const itemCount = itemsOfType.size;
        return itemCount >= 5;
      }
    },
    getSharedTip(SharedTipIdentifiers.ORGANIZE_EVENTS, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.DRAG_TO_TIMELINE, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.SET_EVENT_DATE_TIMELINE, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.SET_EVENT_DATE_INSPECTOR, GROUP_ID)
  ];
} else if (isMobile) {
  tips = [
    {
      id: idGenerator("ADD_INITIAL_EVENTS"),
      label: `Add ${Placeholders.EVENT_PLURAL_LABEL} that are important in your project`,
      steps: [
        getSwitchViewInstruction(ITEM_CARD_SEQUENCE_LIST_VIEW),
        { label: 'Tap "+ Add" in the top corner', isNumbered: true },
        { label: `Type a label for your first ${Placeholders.EVENT_LABEL} and press enter.`, isNumbered: true },
        { label: `Click the back arrow to go back to Chronological List View`, isNumbered: true },
        { label: `Repeat to add more ${Placeholders.EVENT_PLURAL_LABEL} to your project.`, isNumbered: true },
        { label: "Don't worry about order yet... just get your ideas down!", isNumbered: false }
      ],
      accessGate: null,
      gateMessage: null,
      canSkip: false,
      requiresDismissal: true,
      actionInstruction: `Add at least 5 ${Placeholders.EVENT_PLURAL_LABEL} to continue`,
      dismissalGate: state => {
        // At least 5 events
        const eventTypeId = definitionSelectors.getDefaultSequenceType(state);
        const itemsOfType = itemSelectors.getItemIdsOfType(state, eventTypeId);
        const itemCount = itemsOfType.size;
        return itemCount >= 5;
      }
    },
    getSharedTip(SharedTipIdentifiers.ORGANIZE_EVENTS, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.SET_EVENT_DATE_INSPECTOR, GROUP_ID),
    {
      id: idGenerator("ROTATE_TO_TIMELINE_VIEW"),
      label: `Rotate your screen to view your project timeline`,
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Your ${Placeholders.EVENT_PLURAL_LABEL} with dates will be arranged into a horizontal timeline layout`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      canSkip: true,
      requiresDismissal: true
    },
    getSharedTip(SharedTipIdentifiers.SET_EVENT_DATE_TIMELINE, GROUP_ID)
  ];
}

export default {
  id: GROUP_ID,
  label: GROUP_LABEL,
  description: GROUP_DESCRIPTION,
  accessGate: GROUP_GATE,
  gateMessage: GROUP_GATE_MESSAGE,
  applicableGate: GROUP_APPLICABLE_GATE,
  gateApplicableMessage: GROUP_APPLICABLE_GATE_MESSAGE,
  completedMessage: GROUP_COMPLETED_MESSAGE,
  links: GROUP_LINKS,
  tips
};
